/* eslint-disable camelcase */
import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Luci_Shopping_Api_Models_OrderItem } from 'types/api/v7.1';
import InvoiceDetails from '../../types/InvoiceDetails';
import TaxStrategyType from '../../types/TaxStrategyType';
import { finalizeOrder, useGetInvoice } from '../../api/ShoppingApi';
import CostCalculationTable from '../../components/CostCalculationTable';
import ProductAccordion from '../../components/ProductAccordion/ProductAccordion';
import ProductLineItem from '../../components/ProductLineItem';
import BundlesType from '../../types/BundlesType';
import Invoice from '../../types/Invoice';
import OrderItem from '../../types/OrderItem';
import { OrderStatus } from '../../types/Order';
import PaymentFormInfo from '../../types/PaymentFormInfo';
import GAEvent from '../../utilities/analytics';
import CustomerInfo from '../../types/CustomerInfo';

type Props = {
  invoice: Invoice;
  invoiceDetails: InvoiceDetails;
  invoiceId: string;
  taxStrategy: TaxStrategyType;
  termsOfUseUrl: string;
  privacyPolicyUrl: string;
  orderCancelled?: boolean;
  orderExpired?: boolean;
  orderPastDue?: boolean;
  handleChange: (customerInfo: CustomerInfo) => void;
  setPaymentFormInfo: (paymentFormInfo: PaymentFormInfo) => void;
  setInvoiceDetails: (invoice: Invoice) => void;
};

function InvoicePortalSummary({
  invoice,
  invoiceDetails,
  invoiceId,
  taxStrategy,
  orderCancelled = false,
  orderExpired = false,
  orderPastDue = false,
  handleChange,
  setPaymentFormInfo,
  setInvoiceDetails,
  termsOfUseUrl,
  privacyPolicyUrl,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [finalizeOrderError, setFinalizeOrderError] = useState<boolean>(false);
  const { mutateInvoice } = useGetInvoice(invoiceId);

  let orderPaymentCheck = 'Proceed to Billing';

  if (!invoiceDetails?.order || loading) {
    return (
      <Box
        role="status"
        sx={{
          margin: '4rem auto',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        className="loading"
      >
        <CircularProgress data-testid="loading-spinner" color="info" />
      </Box>
    );
  }
  if (
    !(
      invoiceDetails.order.orderTotal !== undefined &&
      invoiceDetails.order.orderTotal > 0
    )
  ) {
    orderPaymentCheck = 'Proceed to Shipping';

    if (invoiceDetails.order.fulfillmentMethod !== 'Shipping') {
      orderPaymentCheck = 'Complete Order';
    }
  }

  const onSubmit = async (): Promise<void> => {
    setLoading(true);
    setFinalizeOrderError(false);

    handleChange({
      phone: undefined,
      shippingAddress: invoiceDetails.order?.shipToAddress
        ? {
            firstName: invoiceDetails.order.customer?.firstName ?? '',
            lastName: invoiceDetails.order.customer?.lastName ?? '',
            line1: invoiceDetails.order.shipToAddress.line1,
            line2: invoiceDetails.order.shipToAddress.line2,
            city: invoiceDetails.order.shipToAddress.city,
            state: invoiceDetails.order.shipToAddress.state,
            zip: invoiceDetails.order.shipToAddress.zip ?? '',
            country: invoiceDetails.order.shipToAddress.country,
          }
        : {
            firstName: '',
            lastName: '',
            line1: '',
            city: '',
            state: '',
            zip: '',
            country: '',
          },
      billingAddress: invoiceDetails.order?.billingAddress
        ? {
            firstName: invoiceDetails.order.customer?.firstName ?? '',
            lastName: invoiceDetails.order.customer?.lastName ?? '',
            line1: invoiceDetails.order.billingAddress.line1,
            line2: invoiceDetails.order.billingAddress.line2,
            city: invoiceDetails.order.billingAddress.city,
            state: invoiceDetails.order.billingAddress.state,
            zip: invoiceDetails.order.billingAddress.zip ?? '',
            country: invoiceDetails.order.billingAddress.country,
          }
        : {
            firstName: '',
            lastName: '',
            line1: '',
            city: '',
            state: '',
            zip: '',
            country: '',
          },
    });

    try {
      finalizeOrder(invoiceId, '', null, null)
        .then((response) => {
          const finalizedInvoiceWithResponse = {
            ...invoice,
            invoiceDetails: {
              ...invoiceDetails,
              order: {
                ...response.order,
                status: 'Completed' as OrderStatus,
              },
            },
          };

          mutateInvoice();
          if (invoice.tenantId !== undefined) {
            GAEvent(orderPaymentCheck, invoice.tenantId as number);
          }
          if (response?.paymentFormInfo) {
            setPaymentFormInfo(response.paymentFormInfo);
          }
          setInvoiceDetails(finalizedInvoiceWithResponse);
        })
        .then(() => {
          navigate(`/invoice/${invoiceId}/confirmation`);
        })
        .catch((error) => {
          Sentry.captureMessage(error, 'warning');
          setFinalizeOrderError(true);
          setLoading(false);
        });
    } catch (error) {
      Sentry.captureException(error);
      setFinalizeOrderError(true);
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={0}>
      {invoiceDetails?.order?.orderItems?.map((item: OrderItem) => (
        <div
          key={`${item.type}-${
            item.itemNumberRetailerAssigned ?? item.id ?? 'unassigned'
          }-${item.sku}`}
        >
          {item.type === 'Bundle' ? (
            <Box sx={{ margin: '1rem' }}>
              <ProductAccordion lineItem={item as BundlesType} />
            </Box>
          ) : (
            <ProductLineItem
              id={parseInt(item.itemNumberRetailerAssigned ?? '', 10) ?? undefined}
              type={item.type as Luci_Shopping_Api_Models_OrderItem.type}
              image={item.image ?? undefined}
              description={item?.description || ''}
              name={item.name || ''}
              sku={item.sku || ''}
              quantity={item.quantity ?? 0}
              isFree={item.isFree || false}
              isReward={item.isReward || false}
              salePrice={item.salePrice ?? 0}
            />
          )}
        </div>
      ))}
      <Grid item xs={12}>
        <CostCalculationTable
          order={invoiceDetails.order}
          estimates={invoiceDetails}
          taxStrategy={taxStrategy}
        />
      </Grid>
      <Grid item xs={12}>
        {finalizeOrderError && (
          <Alert severity="error" sx={{ margin: '1rem 0' }}>
            Something went wrong. Please try refreshing your page.
          </Alert>
        )}
      </Grid>
      {orderPaymentCheck === 'Complete Order' ? (
        <Grid item xs={12} className="mt-3">
          <Typography variant="body2" color="textSecondary">
            <small>
              By clicking the {orderPaymentCheck} button, you confirm that you
              have read and understood and accept our{' '}
              <Link
                to={termsOfUseUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link
                to={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </Link>
              .
            </small>
          </Typography>
          <Button
            className="my-3"
            color="primary"
            variant="contained"
            fullWidth
            data-testid="form-submit-button"
            disabled={loading}
            onClick={onSubmit}
          >
            {loading ? (
              <CircularProgress
                size="1.5rem"
                color="inherit"
                data-testid="loading-spinner"
              />
            ) : (
              orderPaymentCheck
            )}
          </Button>
          {orderCancelled && orderExpired && !orderPastDue && (
            <Button
              className="my-3"
              color="primary"
              variant="contained"
              fullWidth
              data-testid="form-submit-button"
              disabled={loading}
              onClick={onSubmit}
            >
              {loading ? (
                <CircularProgress
                  size="1.5rem"
                  color="inherit"
                  data-testid="loading-spinner"
                />
              ) : (
                orderPaymentCheck
              )}
            </Button>
          )}
        </Grid>
      ) : (
        <Grid item xs={12} className="my-4">
          <Button
            color="primary"
            variant="contained"
            fullWidth
            data-testid="form-submit-button"
            disabled={loading || !!orderCancelled || (orderExpired && !orderPastDue)}
            component={Link}
            to={`/invoice/${invoiceId}/address`}
          >
            {loading ? (
              <CircularProgress
                size="1.5rem"
                color="inherit"
                data-testid="loading-spinner"
              />
            ) : (
              orderPaymentCheck
            )}
          </Button>
        </Grid>
      )}
    </Grid>
  );
}

export default InvoicePortalSummary;
